import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    AlertTriangle, Check, Sparkles, ArrowRight, Crown, Briefcase, Users,
    Infinity as InfinityIcon, Download, Rocket, ShieldCheck, Loader2
} from "lucide-react";
import { apiBase } from "@/lib/apiBase";
import StudioDescription from "@/components/founding/StudioDescription";
import { ELSS_SCOPE_DISCLAIMER } from "@/components/founding/MiniDisclaimer";
import WaitlistForm from "@/components/founding/WaitlistForm";

const API = apiBase();
const CLIENT_KEY = "fsrs_client_id";

function getClientId() {
    if (typeof window === "undefined") return null;
    let id = localStorage.getItem(CLIENT_KEY);
    if (!id) {
        id = `fsrs_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
        localStorage.setItem(CLIENT_KEY, id);
    }
    return id;
}

const SOLD_PLANS = new Set(["starter", "founding_beta"]);
const ACTIVE_PLAN_DETAIL = "You already have an active plan";

function detailText(detail) {
    if (typeof detail === "string") return detail.trim();
    if (detail && typeof detail === "object" && typeof detail.message === "string") {
        return detail.message.trim();
    }
    return "";
}

function isActivePlanDetail(detail) {
    return detailText(detail).replace(/\.+$/, "").toLowerCase() === ACTIVE_PLAN_DETAIL.toLowerCase();
}

function checkoutFailureMessage(error) {
    if (!error?.response) {
        return "We couldn't reach checkout. Check your connection and try again.";
    }
    const status = error.response.status;
    const detail = detailText(error.response.data?.detail);
    const safe = detail && detail.length <= 180 ? detail : "";
    if (status === 400) {
        return safe || "Checkout could not be started. Check the plan and try again.";
    }
    if (status >= 500) {
        return "Checkout is unavailable right now. Try again in a moment.";
    }
    return safe || "Checkout could not be started. Try again.";
}

function soldPlan(value) {
    const key = String(value || "").trim().toLowerCase();
    return SOLD_PLANS.has(key) ? key : null;
}

function planFromTierLabel(label) {
    const text = String(label || "").trim().toLowerCase();
    if (text.startsWith("starter")) return "starter";
    if (text.startsWith("founding beta")) return "founding_beta";
    return null;
}

function activePlanCopy(plan) {
    if (plan === "starter") {
        return `${ACTIVE_PLAN_DETAIL}. Starter is your current plan.`;
    }
    if (plan === "founding_beta") {
        return `${ACTIVE_PLAN_DETAIL}. Founding Beta is your current plan.`;
    }
    return `${ACTIVE_PLAN_DETAIL}. There's no need to start another subscription.`;
}

async function loadActivePlan() {
    const headers = { "X-Client-Id": getClientId() };
    const [me, balance] = await Promise.all([
        axios.get(`${API}/billing/me`, { headers }).catch(() => null),
        axios.get(`${API}/studio/usage/balance`, { headers }).catch(() => null),
    ]);
    return soldPlan(me?.data?.tier_key || me?.data?.profile?.plan)
        || planFromTierLabel(balance?.data?.tier_label);
}

async function startCheckout(packageId) {
    const clientId = getClientId();
    const r = await axios.post(`${API}/payments/v1/checkout/session`, {
        package_id: packageId,
        origin_url: window.location.origin,
        client_id: clientId,
    }, {
        headers: { "X-Client-Id": clientId },
    });
    if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
    }
    throw new Error("No checkout URL returned from server");
}

const COMING_SOON_KEYS = new Set(["professional", "firm"]);

const TIERS = [
    {
        key: "starter", name: "STARTER", price: "$99", period: "/month",
        bullets: ["5 SRUs included each month", "Watermarked exports", "Single seat"],
        cta: "Get Starter — $99/month",
    },
    {
        key: "founding_beta", name: "FOUNDING BETA", price: "$249", period: "/month",
        bullets: ["15 SRUs included each month", "Lifetime badge", "Priority access"],
        cta: "Claim $249/month Founding Spot",
    },
    {
        key: "professional", name: "PROFESSIONAL", comingSoon: true,
        bullets: ["30 SRUs included each month", "Team seats (up to 3)"],
    },
    {
        key: "firm", name: "FIRM PLAN", comingSoon: true,
        bullets: ["100 SRUs included each month", "Team seats (up to 5)", "Shared Dashboard"],
    },
];

export default function Pricing() {
    const [pendingPkg, setPendingPkg] = useState(null);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [planNotice, setPlanNotice] = useState("");
    const [blocksCheckout, setBlocksCheckout] = useState(false);
    const [checkoutError, setCheckoutError] = useState("");

    useEffect(() => {
        let cancelled = false;
        loadActivePlan().then((plan) => {
            if (cancelled || !plan) return;
            setCurrentPlan(plan);
            setPlanNotice(activePlanCopy(plan));
        }).catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    const checkoutLocked = Boolean(currentPlan) || blocksCheckout;

    const handleClaim = async (packageKey) => {
        if (COMING_SOON_KEYS.has(packageKey)) return;
        if (checkoutLocked || pendingPkg) return;
        if (!termsAccepted) {
            toast.error("Please agree to the Terms of Service before proceeding.");
            return;
        }
        setPendingPkg(packageKey);
        setCheckoutError("");
        try {
            await startCheckout(packageKey);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 409 || isActivePlanDetail(detail)) {
                setBlocksCheckout(true);
                setPlanNotice((current) => current || activePlanCopy(currentPlan));
                setCheckoutError("");
                return;
            }
            const message = checkoutFailureMessage(e);
            setCheckoutError(message);
            toast.error(message);
        } finally {
            setPendingPkg(null);
        }
    };

    return (
        <section className="bg-black py-20 text-white min-h-screen">
            <div className="mx-auto max-w-7xl px-6">
                <StudioDescription className="mb-8" testId="pricing-studio-description" />
                <div
                    className="mb-8 flex items-start gap-1.5 border fsrs-caution fsrs-caution-fill px-2.5 py-1 text-lg font-mono tracking-wide leading-snug"
                    data-testid="pricing-elss-disclaimer"
                    role="note"
                >
                    <AlertTriangle
                        className="mt-1.5 h-3 w-3 shrink-0"
                        strokeWidth={2.25}
                    />
                    <p className="min-w-0">{ELSS_SCOPE_DISCLAIMER}</p>
                </div>
                {planNotice ? (
                    <div
                        role="alert"
                        data-testid="active-plan-message"
                        className="mb-8 border fsrs-caution fsrs-caution-fill px-5 py-4"
                    >
                        <p className="text-lg font-bold">{planNotice}</p>
                    </div>
                ) : null}
                {checkoutError ? (
                    <div
                        role="alert"
                        data-testid="checkout-error"
                        className="mb-8 border border-red-500/50 bg-red-500/10 px-5 py-4"
                    >
                        <p className="text-lg font-bold text-white">{checkoutError}</p>
                    </div>
                ) : null}
                <div className="grid lg:grid-cols-4 gap-6">
                    {TIERS.map((p) => (
                        <div
                            key={p.key}
                            data-coming-soon={p.comingSoon ? "true" : undefined}
                            className="border border-white/10 p-8 flex flex-col bg-black"
                        >
                            <h2 className="text-xl font-bold mb-2">{p.name}</h2>
                            {p.comingSoon ? (
                                <div className="mb-8 text-xl font-semibold tracking-[0.14em] uppercase text-slate-400">
                                    Coming soon
                                </div>
                            ) : (
                                <div className="text-5xl font-extrabold mb-8">{p.price}<span className="text-xl font-semibold text-slate-400">{p.period}</span></div>
                            )}
                            <ul className="space-y-4 mb-10 flex-1">
                                {p.bullets.map((b, i) => (
                                    <li key={i} className="text-lg text-slate-300 flex items-start gap-2">
                                        <Check className="w-5 h-5 text-emerald-500 mt-1" /> {b}
                                    </li>
                                ))}
                            </ul>
                            {p.comingSoon || checkoutLocked ? (
                                <div className="mb-6 h-8" aria-hidden="true" />
                            ) : (
                                <div className="mb-6 flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                        data-testid={`pricing-terms-${p.key}`}
                                        className="mt-1.5 h-6 w-6 rounded border-white/20 bg-white/5 text-red-600"
                                    />
                                    <span className="text-slate-300 text-base leading-snug">
                                        Agree to <Link to="/terms" target="_blank" className="underline">Terms & Disclaimer</Link>
                                    </span>
                                </div>
                            )}
                            {p.comingSoon ? (
                                <button
                                    type="button"
                                    disabled
                                    aria-disabled="true"
                                    className="w-full border border-white/15 py-4 text-slate-400 font-bold cursor-not-allowed"
                                >
                                    Coming soon
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    disabled={checkoutLocked || pendingPkg === p.key}
                                    aria-disabled={checkoutLocked || pendingPkg === p.key ? "true" : undefined}
                                    data-testid={`pricing-cta-${p.key}`}
                                    onClick={() => handleClaim(p.key)}
                                    className={
                                        checkoutLocked
                                            ? "w-full border border-white/15 py-4 text-slate-300 font-bold cursor-not-allowed"
                                            : "w-full border border-red-500 py-4 text-red-500 font-bold hover:bg-red-500/10 disabled:opacity-60"
                                    }
                                >
                                    {pendingPkg === p.key ? (
                                        <Loader2 className="animate-spin mx-auto" />
                                    ) : checkoutLocked ? (
                                        currentPlan === p.key ? "Current plan" : "Plan active"
                                    ) : (
                                        p.cta
                                    )}
                                </button>
                            )}
                            {p.comingSoon ? <WaitlistForm plan={p.key} /> : null}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
